import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '~/components/ui/button';
import type { AppliedProductFilters } from '~/features/experiments/types/product-metadata.types';
import { listCatalogProducts } from '~/features/explorer/services/products-catalog.service';
import { useStartScopedCatalogImport } from '~/features/onboarding/hooks/use-catalog-import';
import { SCOPED_IMPORT_MAX_PRODUCTS } from '~/features/onboarding/types/catalog-import.types';
import type { ConnectionType } from '~/features/onboarding/types/connection.types';
import { isCatalogResyncVisible } from '~/features/product-catalog/utils/catalog-resync-visibility';

interface CatalogResyncButtonProps {
  /** Selected catalog connection id. */
  connectionId: string;
  /** Connector code of the selected catalog — resync is GMC-only. */
  connectorCode: string;
  /** Selected sync/revision (null = latest). */
  syncId: string | null;
  /** Applied catalog filters mirrored up from the products table. */
  filters: AppliedProductFilters;
  /** Server total for the filtered scope — gates the ≤50 visibility. */
  skuCount: number;
}

/**
 * Floating action on the Product Catalog page, shown only when applied filters
 * narrow the catalog to a small set (≤ `SCOPED_IMPORT_MAX_PRODUCTS`). Resyncs
 * exactly those filtered products through the same scoped-import API onboarding
 * uses: it pages the filtered products (one page covers the capped set) and
 * passes their **external** product ids (the GMC resource name, e.g.
 * `accounts/…/products/…`) as `productIds`. GMC-only, matching the scoped-import
 * backend, which keys on the external id, not the internal row id.
 */
export function CatalogResyncButton({
  connectionId,
  connectorCode,
  syncId,
  filters,
  skuCount,
}: CatalogResyncButtonProps) {
  const { t } = useTranslation('product-catalog');
  const [isPreparing, setIsPreparing] = useState(false);
  const startResync = useStartScopedCatalogImport();

  // Only surface when a filter narrows the catalog to a resync-able subset.
  if (!isCatalogResyncVisible({ connectorCode, filters, skuCount })) return null;

  const isBusy = isPreparing || startResync.isPending;

  const handleResync = async () => {
    setIsPreparing(true);
    try {
      // Collect the filtered products. The button only shows at ≤50, so a single
      // page of SCOPED_IMPORT_MAX_PRODUCTS covers the whole filtered set.
      const res = await listCatalogProducts({
        connectionId,
        syncId,
        filters,
        offset: 0,
        limit: SCOPED_IMPORT_MAX_PRODUCTS,
      });
      // The scoped import keys on the external product id (the GMC resource name),
      // not the internal row id; filter out any product missing one.
      const productIds = res.data.products
        .map((p) => p.externalId)
        .filter((id): id is string => !!id)
        .slice(0, SCOPED_IMPORT_MAX_PRODUCTS);
      if (productIds.length === 0) {
        toast.error(t('resync.empty'));
        return;
      }
      startResync.mutate(
        { connectionId, connectorCode: connectorCode as ConnectionType, productIds },
        {
          onSuccess: () => {
            toast.success(t('resync.started', { count: productIds.length }));
          },
        },
      );
    } catch {
      // The scoped-import mutation toasts its own errors; this only covers the
      // id-gathering fetch.
      toast.error(t('resync.error'));
    } finally {
      setIsPreparing(false);
    }
  };

  return (
    <Button
      type="button"
      onClick={() => {
        void handleResync();
      }}
      isLoading={isBusy}
      className="cursor-pointer gap-2 rounded-full px-5"
    >
      {!isBusy && <RefreshCw aria-hidden="true" className="h-4 w-4" />}
      {t('resync.cta', { count: skuCount })}
    </Button>
  );
}
